import {
    InnerLayout,
    ZonesContent,
    ZonesFilter,
    ZonesHeader,
} from "components";

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { Autocomplete, GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import DraftZonePath from "./draft-zone-path";
import deleteIcon from '../../assets/images/remove.png';
import {MAP_API_KEY} from "../../utils/constants";
import {usePlacesLibrary} from "../../utils/use-maps-libraries";
import {breadcrumbIcon, plusIcon} from "../../svgs";
import {Breadcrumb, Button} from "antd";
import {setCoordinates, setModalName} from "../../store/actions";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../../store";
import {useHistory} from "react-router-dom";

const libraries:any = ['places'];

function toLatLngPath(list?: { lat?: number; lng?: number; latitude?: number; longitude?: number }[]) {
    if (!list) {
        return [];
    }
    return list
        .map((point) => ({
            lat: Number(point?.lat ?? point?.latitude),
            lng: Number(point?.lng ?? point?.longitude),
        }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
}

function withoutClosingPoint(path: { lat: number; lng: number }[]) {
    if (path.length < 2) {
        return path;
    }
    const first = path[0];
    const last = path[path.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) {
        return path.slice(0, -1);
    }
    return path;
}

function fitMapToPath(map: google.maps.Map | undefined, path: { lat: number; lng: number }[]) {
    if (!map || path.length < 3 || !window.google?.maps) {
        return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
}

export default function EditZoneForm() {
    const dispatch = useDispatch();
    const {
        zones:{selectedZone}
    } = useSelector<AppState, AppState>((state) => state);
    const mapRef:any = useRef();
    const polygonRefs:any = useRef([]);
    const activePolygonIndex:any = useRef();
    const autocompleteRef:any = useRef();

    const history = useHistory();
    const onClickBack = useCallback(() => {
        history.goBack();
    }, [history]);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: MAP_API_KEY,
        libraries
    });
    const placesReady = usePlacesLibrary(isLoaded);
    const [isDrawing, setIsDrawing] = useState(false);
    const existingPath = useMemo(
        () => toLatLngPath(selectedZone?.latLngList),
        [selectedZone]
    );
    const savedPath = useMemo(() => withoutClosingPoint(existingPath), [existingPath]);
    const [draftPath, setDraftPath] = useState<{ lat: number; lng: number }[]>(
        savedPath.length >= 3 ? savedPath : []
    );
    const [polygons, setPolygons] = useState<{ lat: number; lng: number }[][]>([]);

    const defaultCenter:any = existingPath[0] || {
        lat: 31.582045,
        lng: 74.329376,
    }
    const [center] = useState(defaultCenter);

    useEffect(() => {
        if (savedPath.length < 3) {
            return;
        }
        setDraftPath(savedPath);
        setPolygons([]);
        setIsDrawing(false);
        fitMapToPath(mapRef.current, savedPath);
    }, [savedPath]);

    const containerStyle:any = {
        width: '100%',
        height: '400px',
    }

    const autocompleteStyle:any = {
        boxSizing: 'border-box',
        border: '1px solid transparent',
        width: '240px',
        height: '38px',
        padding: '0 12px',
        borderRadius: '3px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
        fontSize: '14px',
        outline: 'none',
        textOverflow: 'ellipses',
        position: 'absolute',
        right: '8%',
        top: '11px',
        marginLeft: '-120px',
    }

    const deleteIconStyle:any = {
        cursor: 'pointer',
        backgroundImage: `url(${deleteIcon})`,
        height: '24px',
        width: '24px',
        marginTop: '3px',
        backgroundColor: '#fff',
        position: 'absolute',
        top: "2px",
        left: "52%",
        zIndex: 1
    }

    const polygonOptions = {
        fillOpacity: 0.3,
        fillColor: '#ff0000',
        strokeColor: '#ff0000',
        strokeWeight: 2,
        draggable: true,
        editable: true
    }

    const onLoadMap = (map: any) => {
        mapRef.current = map;
        fitMapToPath(map, existingPath);
    }

    const onLoadPolygon = (polygon:any, index:any) => {
        polygonRefs.current[index] = polygon;
    }

    const onClickPolygon = (index:any) => {
        activePolygonIndex.current = index;
    }

    const onLoadAutocomplete = (autocomplete:any) => {
        autocompleteRef.current = autocomplete;
    }

    const onPlaceChanged = () => {
        const { geometry } = autocompleteRef.current.getPlace();
        const bounds = new window.google.maps.LatLngBounds();
        if (geometry.viewport) {
            bounds.union(geometry.viewport);
        } else {
            bounds.extend(geometry.location);
        }
        mapRef.current.fitBounds(bounds);
    }

    const onMapClick = (event: google.maps.MapMouseEvent) => {
        if (!isDrawing || !event.latLng) {
            return;
        }
        const nextPoint = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        setDraftPath((path) => [...path, nextPoint]);
    }

    const onMoveDraftPoint = (index: number, point: { lat: number; lng: number }) => {
        setDraftPath((path) => path.map((item, itemIndex) => itemIndex === index ? point : item));
    }

    const finishDraft = () => {
        if (draftPath.length < 3) {
            window.alert("Add at least 3 points to update the zone.");
            return;
        }
        const startPoint = draftPath[0];
        const nextPath = [...draftPath, startPoint];
        setPolygons([nextPath]);
        setDraftPath([]);
        setIsDrawing(false);
        fitMapToPath(mapRef.current, nextPath);
    }

    const onDeleteDrawing = () => {
        const filtered = polygons.filter((polygon, index) => index !== activePolygonIndex.current)
        setPolygons(filtered)
    }

    const onEditPolygon = (index: number) => {
        const polygonRef = polygonRefs.current[index];
        if (polygonRef) {
            const coordinates = polygonRef.getPath()
                .getArray()
                .map((latLng: { lat: () => any; lng: () => any; }) => ({ lat: latLng.lat(), lng: latLng.lng() }));
            if (coordinates.length < 3) {
                return;
            }

            const allPolygons = [...polygons];
            allPolygons[index] = coordinates;
            setPolygons(allPolygons)
            console.log("Edit Cordinates : ", coordinates)
        }
    }

    const editZone = () => {
        const drawnPath = draftPath.length >= 3
            ? [...draftPath, draftPath[0]]
            : polygons.find((polygon) => polygon.length >= 3);
        if (!drawnPath) {
            window.alert("Please select Polygon First!");
            return;
        }
        if (draftPath.length >= 3) {
            setPolygons([drawnPath]);
            setDraftPath([]);
            setIsDrawing(false);
        }
        dispatch(setCoordinates(drawnPath as []))
        dispatch(setModalName("edit_zone_modal"));
    }

    return (
        isLoaded
            ?<>
                <section className="__table-container" style={{ marginTop: "20px"}}>
                    <div style={{ width: "50px" }}></div>
                    <Breadcrumb className="vehicle-route" separator={breadcrumbIcon} style={{ marginLeft: "-15px"}}>
                        <Breadcrumb.Item>
                            <Button type="link" className="vehicles-link" onClick={onClickBack}>
                                Zone
                            </Button>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Edit Zone</Breadcrumb.Item>
                    </Breadcrumb>
                </section>
                <div>
                    <p>The current zone is shown on the map. Drag its corners to change it, or use the polygon tool to draw a new outline, then click Update Zone.</p>
                    <div>
                        <span style={{display: 'inline-block'}}>
                            <div style={{width: '16px', height: '16px', overflow: 'hidden', position: 'relative'}}>
                                <img alt="" src="https://maps.gstatic.com/mapfiles/drawing.png" draggable="false" style={{position: 'absolute', left: '0px', top: '-64px', userSelect: 'none', border: '0px', padding: '0px', margin: '0px', maxWidth: 'none', width: '16px', height: '192px'}} />
                            </div>
                        </span>
                        <span> Click this icon on the map. Each click adds a red corner and connects it to the previous one. Drag a corner to move it, or click the first corner to close the zone.</span>
                    </div>
                    <div>
                        <span style={{display: 'inline-block'}}>
                            <div style={{width: '16px', height: '16px', overflow: 'hidden', position: 'relative'}}>
                                <img alt="" src="https://maps.gstatic.com/mapfiles/drawing.png" draggable="false" style={{position: 'absolute', left: '0px', top: '-144px', userSelect: 'none', border: '0px', padding: '0px', margin: '0px', maxWidth: 'none', width: '16px', height: '192px'}} />
                            </div>
                        </span>
                        <span> Click this icon to move around the map. </span>
                    </div>
                </div>
                <div className='map-container' style={{ position: 'relative' }}>

                    {
                        polygons.some((polygon) => polygon.length > 0)
                        &&
                        <div
                            onClick={onDeleteDrawing}
                            title='Delete shape'
                            style={deleteIconStyle}>
                        </div>
                    }
                    <div
                        style={{
                            position: 'absolute',
                            zIndex: 2,
                            top: 8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            background: '#fff',
                            borderRadius: 2,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                        }}
                    >
                        <button
                            type="button"
                            title="Draw a zone"
                            onClick={() => {
                                setDraftPath([]);
                                setIsDrawing(true);
                            }}
                            style={{ width: 32, height: 32, border: 0, background: isDrawing ? '#e6f4ff' : '#fff', cursor: 'pointer' }}
                        >
                            <span style={{ display: 'inline-block', width: 16, height: 16, overflow: 'hidden', position: 'relative' }}>
                                <img alt="" src="https://maps.gstatic.com/mapfiles/drawing.png" draggable={false} style={{ position: 'absolute', left: 0, top: -64, width: 16, height: 192 }} />
                            </span>
                        </button>
                        <button
                            type="button"
                            title="Move the map"
                            onClick={() => setIsDrawing(false)}
                            style={{ width: 32, height: 32, border: 0, background: !isDrawing ? '#e6f4ff' : '#fff', cursor: 'pointer' }}
                        >
                            <span style={{ display: 'inline-block', width: 16, height: 16, overflow: 'hidden', position: 'relative' }}>
                                <img alt="" src="https://maps.gstatic.com/mapfiles/drawing.png" draggable={false} style={{ position: 'absolute', left: 0, top: -144, width: 16, height: 192 }} />
                            </span>
                        </button>
                        {isDrawing && draftPath.length >= 3 && (
                            <button
                                type="button"
                                onClick={finishDraft}
                                style={{ border: 0, background: '#fff', cursor: 'pointer', padding: '0 8px', fontWeight: 600 }}
                            >
                                Done
                            </button>
                        )}
                    </div>
                    <GoogleMap
                        zoom={12}
                        center={center}
                        onLoad={onLoadMap}
                        onClick={onMapClick}
                        mapContainerStyle={containerStyle}
                        options={{ disableDoubleClickZoom: isDrawing }}
                    >
                        {
                            polygons.filter((iterator) => iterator.length >= 3).map((iterator, index) => (
                                <Polygon
                                    key={index}
                                    onLoad={(event) => onLoadPolygon(event, index)}
                                    onMouseDown={() => onClickPolygon(index)}
                                    onMouseUp={() => onEditPolygon(index)}
                                    onDragEnd={() => onEditPolygon(index)}
                                    options={polygonOptions}
                                    paths={iterator}
                                    draggable
                                    editable
                                />
                            ))
                        }
                        <DraftZonePath
                            path={draftPath}
                            onMovePoint={onMoveDraftPoint}
                            onClose={finishDraft}
                        />
                        {placesReady && (
                        <Autocomplete
                            onLoad={onLoadAutocomplete}
                            onPlaceChanged={onPlaceChanged}
                        >
                            <input
                                type='text'
                                placeholder='Search Location'
                                style={autocompleteStyle}
                            />
                        </Autocomplete>
                        )}
                    </GoogleMap>
                </div>
                <div style={{textAlign: "end"}}>
                    <Button
                        type="primary"
                        onClick={editZone}
                        style={{marginTop: "15px"}}
                    >
                        Update Zone
                    </Button>
                </div>
            </>
            :
            null
    );
}
